import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getCourseDetail,
  useGetCourseDraftAssetsInfinite,
  useGetCourseDraftById,
} from '@edusense/course-client';
import { customInstance } from '@edusense/course-client/custom-axios';

import {
  CourseContentPayload,
  extractResourcesFromAssetsPayload,
  extractResourcesFromPayload,
  getStatusFromError,
  getStoredResources,
  isResourceAddedEvent,
  mergeResources,
  persistResources,
  ResourceAddedDetail,
  ResourceFile,
} from './ResourcesTabContent.utils';

const FETCH_OPTIONS = { withCredentials: true } as const;

interface UseResourcesTabDataParams {
  courseId: string;
  onResourcesChange?: (resources: ResourceFile[]) => void;
}

interface UseResourcesTabDataReturn {
  resources: ResourceFile[];
  combinedLoading: boolean;
  error: string | null;
  handleRetry: () => void;
  totalSize: number;
  effectiveCourseId: string | null;
}

export const useResourcesTabData = ({
  courseId,
  onResourcesChange,
}: UseResourcesTabDataParams): UseResourcesTabDataReturn => {
  const [resources, setResources] = useState<ResourceFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState<number>(0);
  const [fallbackAssetResources, setFallbackAssetResources] = useState<ResourceFile[]>([]);
  const [isFallbackFetching, setIsFallbackFetching] = useState<boolean>(false);
  const fallbackAttemptedRef = useRef(false);

  const storageCourseId = courseId ?? 'new';
  const effectiveCourseId = courseId && courseId !== 'new' ? courseId : null;

  const {
    data: draftData,
    refetch: refetchDraft,
    error: draftError,
  } = useGetCourseDraftById<CourseContentPayload | undefined, unknown>(effectiveCourseId ?? '', {
    query: {
      enabled: Boolean(effectiveCourseId),
      refetchOnWindowFocus: false,
      retry: false,
    },
    request: FETCH_OPTIONS,
  });

  const assetsQuery = useGetCourseDraftAssetsInfinite(
    effectiveCourseId ?? '',
    {
      page: 0,
      size: 100,
      sort: 'createdAt,desc',
    },
    {
      request: FETCH_OPTIONS,
      query: {
        enabled: Boolean(effectiveCourseId),
        refetchOnWindowFocus: false,
        getNextPageParam: (lastPage) => {
          if (!lastPage) {
            return undefined;
          }

          const currentPage = lastPage.pageable?.page
            ?? lastPage.pageable?.number
            ?? lastPage.number
            ?? 0;
          const totalPages = lastPage.totalPages ?? 0;
          const nextPage = currentPage + 1;
          return nextPage < totalPages ? nextPage : undefined;
        },
        initialPageParam: 0,
      },
    },
  );

  const draftAssetResources = useMemo(() => {
    if (!assetsQuery.data) {
      return [] as ResourceFile[];
    }
    const items = assetsQuery.data.pages.flatMap(page => page?.content ?? []);

    // Log assets data for debugging
    if (items.length > 0) {
      console.log('[ResourcesTab] Assets loaded:', {
        count: items.length,
        firstAsset: items[0],
        courseId: effectiveCourseId
      });
    }

    if (items.length === 0) {
      return [];
    }
    return extractResourcesFromAssetsPayload({ content: items });
  }, [assetsQuery.data, effectiveCourseId]);

  const resolvedAssetResources = draftAssetResources.length > 0
    ? draftAssetResources
    : fallbackAssetResources;

  const requestFallbackAssets = useCallback(() => {
    const publishedCourseId = (draftData as any)?.publishedCourseId;

    if (fallbackAttemptedRef.current) {
      return () => {};
    }

    if (!publishedCourseId) {
      return () => {};
    }

    fallbackAttemptedRef.current = true;
    setIsFallbackFetching(true);

    let isActive = true;
    const controller = new AbortController();

    const fetchFallbackAssets = async () => {
      try {
        const payload = await customInstance({
          url: `/api/courses/${publishedCourseId}/assets`,
          method: 'GET',
          params: {
            page: 0,
            size: 100,
            sort: 'createdAt,desc',
          },
          signal: controller.signal,
        }, FETCH_OPTIONS);

        if (!isActive) {
          return;
        }

        const mapped = extractResourcesFromAssetsPayload(payload);
        setFallbackAssetResources(mapped);
      } catch (fallbackErr) {
        if (!isActive || controller.signal.aborted) {
          return;
        }

        const message = fallbackErr instanceof Error
          ? fallbackErr.message
          : '자료를 불러오는 중 오류가 발생했습니다.';
        setError(message);
        console.error('[ResourcesTab] Fallback asset fetch failed', fallbackErr);
      } finally {
        if (!isActive || controller.signal.aborted) {
          return;
        }
        setIsFallbackFetching(false);
      }
    };

    void fetchFallbackAssets();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [draftData]);

  useEffect(() => {
    if (!effectiveCourseId) {
      return;
    }
    if (!assetsQuery.hasNextPage) {
      return;
    }
    if (assetsQuery.isFetchingNextPage || assetsQuery.isFetching || assetsQuery.isLoading) {
      return;
    }

    void assetsQuery.fetchNextPage();
  }, [
    assetsQuery.hasNextPage,
    assetsQuery.isFetchingNextPage,
    assetsQuery.isFetching,
    assetsQuery.isLoading,
    effectiveCourseId,
  ]);

  useEffect(() => {
    if (!assetsQuery.data) {
      return;
    }

    const hasAssets = assetsQuery.data.pages.some((page) => (page?.content?.length ?? 0) > 0);
    if (!hasAssets) {
      return;
    }

    fallbackAttemptedRef.current = false;
    setFallbackAssetResources([]);
    setIsFallbackFetching(false);
  }, [assetsQuery.data]);

  useEffect(() => {
    fallbackAttemptedRef.current = false;
    setFallbackAssetResources([]);
    setIsFallbackFetching(false);
  }, [effectiveCourseId]);

  useEffect(() => {
    if (!effectiveCourseId) {
      fallbackAttemptedRef.current = false;
      setFallbackAssetResources([]);
      setIsFallbackFetching(false);
      return;
    }

    const status = getStatusFromError(assetsQuery.error);

    if (assetsQuery.error) {
      console.warn('[ResourcesTab] Assets API error:', {
        status,
        error: assetsQuery.error,
        courseId: effectiveCourseId,
      });
    }

    if (!assetsQuery.error || status !== 404) {
      return;
    }

    return requestFallbackAssets();
  }, [assetsQuery.error, effectiveCourseId, requestFallbackAssets]);

  useEffect(() => {
    if (!effectiveCourseId) {
      return;
    }

    if (assetsQuery.isLoading || assetsQuery.isFetching) {
      return;
    }

    if (!assetsQuery.data) {
      return;
    }

    const hasAssets = assetsQuery.data.pages.some((page) => (page?.content?.length ?? 0) > 0);
    if (hasAssets) {
      return;
    }

    return requestFallbackAssets();
  }, [
    assetsQuery.data,
    assetsQuery.isFetching,
    assetsQuery.isLoading,
    effectiveCourseId,
    requestFallbackAssets,
  ]);

  useEffect(() => {
    if (!effectiveCourseId) {
      const stored = getStoredResources(storageCourseId);
      setResources(stored);
      setError(null);
      return;
    }

    const stored = getStoredResources(storageCourseId);
    setResources(stored);

    if (draftData === undefined && draftError === undefined) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        let sectionPayload: CourseContentPayload | undefined = draftData;
        if (!sectionPayload && effectiveCourseId) {
          const status = getStatusFromError(draftError);
          if (status === 404) {
            sectionPayload = await getCourseDetail(effectiveCourseId, FETCH_OPTIONS);
          }
        }

        const sectionResources = sectionPayload
          ? extractResourcesFromPayload(sectionPayload)
          : [];

        const assetResources = resolvedAssetResources;
        const assetErrorStatus = getStatusFromError(assetsQuery.error);
        if (assetErrorStatus && assetErrorStatus !== 404 && assetResources.length === 0) {
          throw assetsQuery.error ?? new Error('자료를 불러오는 중 오류가 발생했습니다.');
        }

        const merged = mergeResources([...sectionResources, ...assetResources], stored);

        // Log final resources for debugging
        console.log('[ResourcesTab] Resources merged:', {
          sectionResourcesCount: sectionResources.length,
          assetResourcesCount: assetResources.length,
          storedCount: stored.length,
          mergedCount: merged.length,
          courseId: effectiveCourseId
        });

        if (!cancelled) {
          setResources(merged);
          persistResources(storageCourseId, merged);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : '자료를 불러오는 중 오류가 발생했습니다.';
          setError(message);
          console.error('[ResourcesTab] Failed to load resources', err);
          toast.error(message);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    assetsQuery.error,
    draftData,
    draftError,
    effectiveCourseId,
    reloadNonce,
    resolvedAssetResources,
    storageCourseId,
  ]);

  useEffect(() => {
    if (!onResourcesChange) {
      return;
    }
    onResourcesChange(resources);
  }, [resources, onResourcesChange]);

  useEffect(() => {
    if (!effectiveCourseId) {
      return;
    }

    const handleResourceAdded = (event: Event) => {
      if (!isResourceAddedEvent(event)) {
        return;
      }

      const detail: ResourceAddedDetail = event.detail;
      const mapped: ResourceFile = {
        id: detail.id || `${Date.now()}-${detail.url}`,
        name: detail.name || detail.url,
        url: detail.url,
        size: detail.size ?? null,
        mimeType: detail.mimeType ?? null,
        uploadedAt: detail.uploadedAt ?? new Date().toISOString(),
        sectionTitle: detail.sectionTitle ?? null,
        lectureTitle: detail.lectureTitle ?? null,
      };

      setResources(prev => {
        const merged = mergeResources([mapped], prev);
        persistResources(storageCourseId, merged);
        return merged;
      });
    };

    window.addEventListener('resourceAdded', handleResourceAdded);
    return () => {
      window.removeEventListener('resourceAdded', handleResourceAdded);
    };
  }, [effectiveCourseId, storageCourseId]);

  const totalSize = useMemo(() => resources.reduce((sum, item) => sum + (item.size ?? 0), 0), [resources]);
  const combinedLoading = Boolean(
    assetsQuery.isLoading
      || assetsQuery.isFetching
      || assetsQuery.isFetchingNextPage
      || isFallbackFetching,
  );

  const handleRetry = useCallback(() => {
    if (!effectiveCourseId) {
      return;
    }

    setError(null);
    setFallbackAssetResources([]);
    fallbackAttemptedRef.current = false;
    setReloadNonce(prev => prev + 1);
    void refetchDraft();
    void assetsQuery.refetch();
  }, [assetsQuery, effectiveCourseId, refetchDraft]);

  return {
    resources,
    combinedLoading,
    error,
    handleRetry,
    totalSize,
    effectiveCourseId,
  };
};
